import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone'

import PrimaryButton from './PrimaryButton';
import OutlineButton from './OutlineButton';
import Icon from '../../assets/icons/icon';

const dateTypes = Object.freeze({
    NORMAL: Symbol('NORMAL'),
    SELECTED: Symbol('SELECTED'),
    RANGE: Symbol('RANGE'),
    TODAY: Symbol('TODAY'),
    EMPTY: Symbol('EMPTY'),
})
const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * @type {DatePickerData}
 */
const emptyData = {
    date: '',
    startOfDay: -1,
    endOfDay: -1,
    dayOfMonth: -1,
    dayOfWeek: -1,
    timezone: '',
}

/**
 * Component for selecing a date range.
 *
 * @param {DateRangePickerProps} props
 * @returns {React.JSX.Element}
 */
function DateRangePicker({title, cancelCallback, saveCallback, dateFormat='MM/DD/YYYY', disabled=false, timezone = defaultTimezone}){
    const [dateLabel, setDateLabel] = useState(moment().tz(timezone).format('MMMM YYYY'))
    const [monthData, setMonthData] = useState([[-1]])
    const [selectedDates, setSelectedDates] = useState({
        start: emptyData,
        end: emptyData
    })

    const [dateKey, setDateKey] = useState('start')
    const [incrementAmount, setIncrementAmount] = useState(0)

    const updateMonthData = () => {
        if(disabled){
            return
        }

        let preBuffer
        let postBuffer

        const m_date = moment().tz(timezone).add(incrementAmount, 'month')

        switch (m_date.date(1).day()) {
            case 1: // monday
                preBuffer = 1
                break;
            case 2: // tuesday
                preBuffer = 2
                break;
            case 3: // wednesday
                preBuffer = 3
                break;
            case 4: // thursday
                preBuffer = 4
                break;
            case 5: // friday
                preBuffer = 5
                break;
            case 6: // saturday
                preBuffer = 6
                break;
            default: // sunday
                preBuffer = 0
                break;
        }
        switch (m_date.endOf('M').day()) {
            case 0: // sunday
                postBuffer = 6
                break;
            case 1: // monday
                postBuffer = 5
                break;
            case 2: // tuesday
                postBuffer = 4
                break;
            case 3: // wednesday
                postBuffer = 3
                break;
            case 4: // thursday
                postBuffer = 2
                break;
            case 5: // friday
                postBuffer = 1
                break;
            default: //satuday
                postBuffer = 0
                break;
        }
        const days = [
            ...Array(preBuffer).keys().map( _ => -1), // represents empty space on the date picker before the first of the current month
            ...[...Array(m_date.daysInMonth()).keys()].map(k => k+1), // represents each day in the current month
            ...Array(postBuffer).keys().map( _ => -1), // represents empty space on the date picker after the last of the current month
        ]

        let data = []
        const container = []

        for (let index = 0; index < days.length; index++) {
            const day = days[index];

            data.push(day)
            if(data.length === 7){
                container.push(data)
                data = []
            }
        }

        setMonthData(container)
    }

    const getDateContentType = (date) => {
        if(disabled){
            return
        }

        const m_date = moment().tz(timezone).add(incrementAmount, 'month')
        const m_cur_date = m_date.date(date)

        if(date > 0){
            if((selectedDates.start.dayOfWeek > 0 && m_cur_date.startOf('D').unix() === selectedDates.start.startOfDay) || (selectedDates.end.dayOfWeek > 0 && m_cur_date.endOf('D').unix() === selectedDates.end.endOfDay)){
                return dateTypes.SELECTED
            } else if (selectedDates.end.dayOfWeek > 0 && selectedDates.start.dayOfWeek > 0 && m_cur_date.startOf('D').unix() > selectedDates.start.startOfDay && m_cur_date.endOf('D').unix() < selectedDates.end.endOfDay) {
                return dateTypes.RANGE
            } else if (moment().tz(timezone).startOf('D').unix() === m_cur_date.startOf('D').unix()) {
                return dateTypes.TODAY
            } else {
                return dateTypes.NORMAL
            }
        }

        return dateTypes.EMPTY
    }

    const selectDate = (date) => {
        if(disabled){
            return
        }

        const m_date = moment().tz(timezone).add(incrementAmount, 'month')

        /**
         * @type {DatePickerData}
         */
        const dateData = {
            date: m_date.date(date).format(dateFormat),
            startOfDay: m_date.date(date).startOf('D').unix(),
            endOfDay: m_date.date(date).endOf('D').unix(),
            dayOfMonth: date,
            dayOfWeek: m_date.date(date).isoWeekday(),
            timezone: m_date.tz(timezone).tz()
        }
        const updatedState = {
            start: dateKey === 'start' ? dateData : selectedDates.start,
            end: dateKey === 'end' ? dateData : selectedDates.end
        }

        // make sure that the start date comes before the end date
        if(updatedState.end.dayOfWeek > 0 && updatedState.start.dayOfWeek > 0 && updatedState.start.startOfDay > updatedState.end.endOfDay){
            const temp = {...updatedState.start}

            updatedState.start = {...updatedState.end}
            updatedState.end = {...temp}
        }

        setSelectedDates(updatedState)

        if(dateKey === 'start'){
            setDateKey('end')
        }
    }
    const deSelectDate = (date) => {
        if(disabled){
            return
        }
        if(date === selectedDates.start.dayOfMonth){
            setSelectedDates({
                start: emptyData,
                end: selectedDates.end
            })
            setDateKey('start')
        } else if(date === selectedDates.end.dayOfMonth){
            setSelectedDates({
                start: selectedDates.start,
                end: emptyData
            })
            setDateKey('end')
        }
    }
    const handleCancel = e => {
        if(disabled){
            return
        }
        setSelectedDates({
            start: emptyData,
            end: emptyData
        })

        setDateKey('start')

        cancelCallback(e, selectedDates)
    }

    useEffect(()=>{
        updateMonthData()
    }, [])

    useEffect(()=>{
        setDateLabel(moment().tz(timezone).add(incrementAmount, 'month').format('MMMM YYYY'))
        updateMonthData()
    }, [incrementAmount])
    return (
        <div style={{ width: '110%', height: '100%', background: 'white', boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03)', borderRadius: 6, outline: '1px #E4E4E4 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', display: 'inline-flex' }}>
            <div style={{ width: 392, alignSelf: 'stretch', paddingTop: 16, paddingBottom: 12, paddingLeft: 20, paddingRight: 16, borderRight: '1px #EAECF0 solid', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'flex' }}>
                    <div style={{ justifyContent: 'flex-start', alignItems: 'flex-end', gap: 4, display: 'inline-flex' }}>
                        <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#707070', fontSize: 14, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>{title}</div>
                    </div>
                    <div style={{ width: 351, overflow: 'hidden', borderRadius: 5, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                        <div style={{ alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', display: 'flex' }}>
                                <div data-state="Enabled" style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10, display: 'inline-flex' }}>
                                    <div style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 8, paddingRight: 4, overflow: 'hidden', borderRadius: 100, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                                        <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 14, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>{dateLabel}</div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                                <div onClick={_ => disabled ? null : setIncrementAmount(incrementAmount - 1)} style={{ cursor: 'pointer', width: 40, height: 40, minWidth: 40, minHeight: 40, overflow: 'hidden', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                                    <div style={{ paddingTop: 6, paddingBottom: 6, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                                        <div style={{ width: 18, height: 18, position: 'relative' }}>
                                            <Icon
                                                name='q-chevron-left-new'
                                                size={18}
                                                height={18}
                                                viewBox={'0 0 18 18'}
                                                color={'none'}
                                                strokeOptions={{
                                                    stroke: '#707070',
                                                    width: '2',
                                                    lineCap: 'round',
                                                    lineJoin: 'round',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div onClick={_ => disabled ? null : setIncrementAmount(incrementAmount + 1)} style={{ cursor: 'pointer', width: 40, height: 40, minWidth: 40, minHeight: 40, overflow: 'hidden', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                                    <div style={{ paddingTop: 6, paddingBottom: 6, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                                        <div style={{ width: 18, height: 18, position: 'relative' }}>
                                            <Icon
                                                name='q-chevron-right-new'
                                                size={18}
                                                height={18}
                                                viewBox={'0 0 18 18'}
                                                color={'none'}
                                                strokeOptions={{
                                                    stroke: '#707070',
                                                    width: '2',
                                                    lineCap: 'round',
                                                    lineJoin: 'round',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ alignSelf: 'stretch', paddingTop: 4, paddingBottom: 4, paddingLeft: 16, paddingRight: 12, justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                            <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                                <div onClick={_ => disabled ? null : setDateKey('start')} style={{ cursor: 'pointer', flex: '1 1 0', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'inline-flex' }}>
                                    <div style={{ alignSelf: 'stretch', minHeight: 44, paddingLeft: 12, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: '#F8F8F8', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', borderRadius: 8, outline: '1px #E4E4E4 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                                        <div style={{ flex: '1 1 0', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'flex' }}>
                                            <div style={{ flex: '1 1 0', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>
                                                {selectedDates.start.date}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ color: '#ADADAD', fontSize: 16, fontFamily: 'Inter', fontWeight: '400', wordWrap: 'break-word' }}>-</div>
                                <div onClick={_ => disabled ? null : setDateKey('end')} style={{ cursor: 'pointer', flex: '1 1 0', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'inline-flex' }}>
                                    <div style={{ alignSelf: 'stretch', minHeight: 44, paddingLeft: 12, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: '#F8F8F8', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', borderRadius: 8, outline: '1px #E4E4E4 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                                        <div style={{ flex: '1 1 0', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'flex' }}>
                                            <div style={{ flex: '1 1 0', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>
                                                {selectedDates.end.date}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ alignSelf: 'stretch', padding: 12, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', display: 'flex' }}>
                            <div style={{ alignSelf: 'stretch', height: 48, justifyContent: 'center', alignItems: 'flex-start', display: 'inline-flex' }}>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>S</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>M</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>T</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>W</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>T</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>F</div>
                                </div>
                                <div style={{ flex: '1 1 0', alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>S</div>
                                </div>
                            </div>
                            {monthData.map((dateRow, dateRowIdx) => (
                                <div key={`month_row_${dateRowIdx}`} style={{ alignSelf: 'stretch', height: 48, justifyContent: 'center', alignItems: 'flex-start', display: 'inline-flex' }}>
                                    {dateRow.map((dateNumber, dateNumberIdx) => (
                                        <>
                                            {
                                                getDateContentType(dateNumber) === dateTypes.NORMAL ? (
                                                    <div onClick={_ => selectDate(dateNumber)} key={`month_row_${dateRowIdx}_date_${dateNumberIdx}`} style={{ cursor: 'pointer', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                                                        <div style={{ width: 40, height: 40, overflow: 'hidden', borderRadius: 100, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                                            <div style={{ width: 40, height: 40, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                                                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', letterSpacing: 0.09, wordWrap: 'break-word' }}>
                                                                    {dateNumber}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : getDateContentType(dateNumber) === dateTypes.RANGE ? (
                                                    <div onClick={_ => selectDate(dateNumber)} key={`month_row_${dateRowIdx}_date_${dateNumberIdx}`} style={{ cursor: 'pointer', width: '100%', height: '100%', position: 'relative', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                                                        <div style={{ width: 40, height: 40, left: 43, top: 44, position: 'absolute', transform: 'rotate(180deg)', transformOrigin: 'top left', background: 'var(--Colors-Background-bg-active, #F8F8F8)', borderRadius: 9999 }} />
                                                        <div style={{ width: 40, height: 40, borderRadius: 9999, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                                                            <div style={{ zIndex: 2, textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '500', wordWrap: 'break-word' }}>
                                                                {dateNumber}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : getDateContentType(dateNumber) === dateTypes.SELECTED ? (
                                                    <div onClick={_ => deSelectDate(dateNumber)} key={`month_row_${dateRowIdx}_date_${dateNumberIdx}`} style={{ cursor: 'pointer', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                                                        <div style={{ width: 40, height: 40, background: '#46C3B2', overflow: 'hidden', borderRadius: 100, justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10, display: 'inline-flex' }}>
                                                            <div style={{ width: 40, height: 40, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                                                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Colors-Foreground-fg-white, white)', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '500', wordWrap: 'break-word' }}>
                                                                    {dateNumber}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : getDateContentType(dateNumber) === dateTypes.TODAY ? (
                                                    <div onClick={_ => selectDate(dateNumber)} key={`month_row_${dateRowIdx}_date_${dateNumberIdx}`} style={{ cursor: 'pointer', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                                                        <div style={{ width: 40, height: 40, overflow: 'hidden', borderRadius: 100, outline: '2px #CCFBEF solid', outlineOffset: '-2px', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                                            <div style={{ width: 40, height: 40, padding: 10, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                                                                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Colors-Foreground-fg-primary-(900), #2F2F2F)', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', letterSpacing: 0.09, wordWrap: 'break-word' }}>
                                                                    {dateNumber}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : getDateContentType(dateNumber) === dateTypes.EMPTY ? (
                                                    <div key={`month_row_${dateRowIdx}_date_${dateNumberIdx}`} style={{ width: '100%', height: '100%', position: 'relative' }} />
                                                ) : null
                                            }
                                        </>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div style={{ maxHeight: 40, alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex', paddingBottom: 12 }}>
                            <OutlineButton
                                buttonLabel='Cancel'
                                clickHandler={handleCancel}
                                disabled={disabled}
                            />
                          <PrimaryButton
                            buttonLabel='Save'
                            clickHandler={e => saveCallback(e, selectedDates)}
                            disabled={disabled || selectedDates.start.dayOfWeek === -1 || selectedDates.end.dayOfWeek === -1}
                            buttonClass={disabled || selectedDates.start.dayOfWeek === -1 || selectedDates.end.dayOfWeek === -1 ? 'q-primary-button-disabled' : 'q-primary-button'}
                        />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default DateRangePicker